import * as React from 'react';
import { VpnSetting, VpnSettingValue, menuLinks } from '@cloud/shared';

import { getLegacyToken } from '@ovpn-ui/utils';
import { NavigationLink } from '@ovpn-ui/navigation';

import { SidebarMenuDot } from './SidebarMenuDot';

type RawLink = { name: NavigationLink['label']; children?: Record<string, RawLink> };

function processLink([key, { name, children, ...linkProps }]: [string, RawLink]) {
    const link: NavigationLink = { ...linkProps, key, label: name };
    if (children) {
        link.children = Object.entries(children).map(processLink);

        if (link.icon) {
            link.icon = React.cloneElement(link.icon, {
                'data-name':
                    name === menuLinks.apiAndLogs.name ? 'api_and_logs' : name.toLowerCase(),
            });
        }
    }

    return link;
}

const menuLinksForNavbar = Object.entries(menuLinks).reduce(
    (acc, entry) => {
        acc[entry[0] as keyof typeof menuLinks] = processLink(entry);
        return acc;
    },
    {} as Record<keyof typeof menuLinks, NavigationLink>,
);

const betaDot = <SidebarMenuDot />;

function omitChildLinks(parent: NavigationLink, keysToOmit?: (boolean | string)[]): NavigationLink {
    if (!keysToOmit?.length) return parent;

    return {
        ...parent,
        children: parent.children!.filter(child => !child.key || !keysToOmit.includes(child.key)),
    };
}

function changeSupportLinkForOwner(docLink: NavigationLink) {
    docLink.children = docLink.children?.map(t => {
        if (t.key === 'support') {
            const legacyToken = getLegacyToken();
            const url = window._env_.SUPPORT_HOST;
            const supportUrl = `${url}${legacyToken ? `?${legacyToken}` : ''}`;

            return {
                ...t,
                target: '_blank',
                href: supportUrl,
            } as NavigationLink;
        }

        return t;
    });

    return docLink;
}

export function makeOwnerLinks({
    isAdmin,
    settings,
}: {
    isAdmin: boolean;
    settings: Record<VpnSettingValue, any>;
}): NavigationLink[] {
    const enableAuditLog = settings[VpnSetting.EnableAuditLog];
    const enableDomainResolutions = settings[VpnSetting.EnableDomainResolutions];

    const apiAndLogsMenu = omitChildLinks(
        {
            ...menuLinksForNavbar.apiAndLogs,
            decorator: betaDot,
        } as NavigationLink,
        [!enableAuditLog && 'auditLogs', !enableDomainResolutions && 'dnsResolutions'],
    );

    const usersMenu = omitChildLinks(
        {
            ...menuLinksForNavbar.users,
            decorator: betaDot,
        } as NavigationLink,
        [],
    );

    const docsMenu = isAdmin
        ? menuLinksForNavbar.documentation
        : changeSupportLinkForOwner(menuLinksForNavbar.documentation);

    return [
        menuLinksForNavbar.status,
        usersMenu,
        menuLinksForNavbar.network,
        menuLinksForNavbar.hosts,
        menuLinksForNavbar.access,
        menuLinksForNavbar.shieldV2,
        menuLinksForNavbar.publishing,
        apiAndLogsMenu,
        menuLinksForNavbar.settings,
        docsMenu,
        menuLinksForNavbar.getConnected,
    ].filter(Boolean) as NavigationLink[];
}

export const userLinks = [menuLinksForNavbar.devices, menuLinksForNavbar.instructions];
